import React, { useState } from "react";

export default function ExpensesTable({ rows }) {
  const [openRowIds, setOpenRowIds] = useState({});

  const toggleDetails = (rowId) => {
    setOpenRowIds((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  };

  const getRowKey = (row, index) => row?.id || `${row?.label || "row"}_${index}`;

  const getDetailsText = (row) => {
    return String(
      row?.autoDetails ||
        row?.adjustmentDetails ||
        row?.installmentDetails ||
        row?.runningExpenseDetails ||
        row?.wpsDetails ||
        ""
    ).trim();
  };

  const renderLabelCell = (row, key) => {
    const detailsText = getDetailsText(row);
    const hasDetails = Boolean(detailsText);

    return (
      <td
        style={{
          ...td,
          cursor: hasDetails ? "pointer" : "default",
          userSelect: hasDetails ? "none" : "auto",
        }}
        onClick={hasDetails ? () => toggleDetails(key) : undefined}
        title={hasDetails ? "Click to show/hide details" : ""}
      >
        <span>{row.label}</span>
        {hasDetails && (
          <span
            style={{
              marginLeft: 8,
              fontSize: 11,
              color: "#2563eb",
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {openRowIds[key] ? "Hide details" : "Details"}
          </span>
        )}
      </td>
    );
  };

  const renderDetailsRow = (row, key) => {
    const detailsText = getDetailsText(row);
    if (!detailsText || !openRowIds[key]) return null;

    return (
      <tr key={`${key}_details`}>
        <td style={detailTd} colSpan={12}>
          <div style={detailBox}>
            <strong>Details:</strong> {detailsText}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div
      style={{
        overflowX: "auto",
        border: "1px solid #ccc",
        borderRadius: "4px",
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          marginBottom: 12,
          fontSize: 13,
        }}
      >
        <thead>
          <tr style={{ backgroundColor: "#f3f4f6" }}>
            <th style={th}>DESCRIPTION</th>
            <th style={thCenter} colSpan={10}>
              COLUMNS
            </th>
            <th style={thRight}>TOTAL</th>
          </tr>
        </thead>

        <tbody>
          {(rows || []).map((r, index) => {
            const key = getRowKey(r, index);
            const normalized = (r.label || "")
              .toUpperCase()
              .replace(/\s+/g, " ")
              .trim();

            const isTraffic = normalized.includes("TRAFFIC");
            const isFineDate = normalized.includes("FINE DATE");
            const isSummary = r.isSummary;

            // --- SUMMARY ROW ---
            if (isSummary) {
              return (
                <React.Fragment key={key}>
                  <tr
                    style={{
                      background: "#f8f9fa",
                      borderTop: "1px solid #d1d5db",
                      borderBottom: "1px solid #d1d5db",
                    }}
                  >
                    <td style={{ ...td, fontWeight: "bold" }}>{r.label}</td>
                    <td colSpan={10} style={td}></td>
                    <td style={{ ...tdRight, fontWeight: "bold" }}>
                      {Number(r.total || 0).toFixed(2)}
                    </td>
                  </tr>
                </React.Fragment>
              );
            }

            // --- TRAFFIC FINES OR FINE DATE (10 columns) ---
            if (isTraffic || isFineDate) {
              return (
                <React.Fragment key={key}>
                  <tr
                    style={{
                      borderTop: "1px solid #d1d5db",
                      borderBottom: "1px solid #d1d5db",
                    }}
                  >
                    {renderLabelCell(r, key)}

                    {[...Array(10)].map((_, i) => {
                      const val = r[`col${i + 1}`] ?? "";

                      return (
                        <td
                          key={i}
                          style={{
                            ...td,
                            width: "6%",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {isFineDate ? (
                            <span>{val}</span>
                          ) : (
                            <span
                              style={{
                                textAlign: "right",
                                display: "block",
                              }}
                            >
                              {val === "" ? "" : Number(val).toFixed(2)}
                            </span>
                          )}
                        </td>
                      );
                    })}

                    <td style={tdRight}>{Number(r.total || 0).toFixed(2)}</td>
                  </tr>
                  {renderDetailsRow(r, key)}
                </React.Fragment>
              );
            }

            // --- NORMAL EXPENSE ROW ---
            const middleValues = [...Array(10)].map((_, i) => r[`col${i + 1}`] ?? "");
            const hasMiddleValues = middleValues.some((v) => String(v || "").trim() !== "");

            return (
              <React.Fragment key={key}>
                <tr
                  style={{
                    borderTop: "1px solid #d1d5db",
                    borderBottom: "1px solid #d1d5db",
                  }}
                >
                  {renderLabelCell(r, key)}

                  {hasMiddleValues ? (
                    middleValues.map((val, i) => (
                      <td
                        key={i}
                        style={{
                          ...td,
                          width: "6%",
                          whiteSpace: "nowrap",
                          fontSize: 12,
                        }}
                        title={i === 2 ? r.description || "" : ""}
                      >
                        {val}
                      </td>
                    ))
                  ) : (
                    <td colSpan={10} style={{ ...td, paddingLeft: 10 }}>
                      <span>{r.description || ""}</span>
                    </td>
                  )}

                  <td style={tdRight}>{Number(r.total || 0).toFixed(2)}</td>
                </tr>
                {renderDetailsRow(r, key)}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const th = {
  border: "1px solid #ccc",
  padding: 8,
  textAlign: "left",
  background: "#f3f4f6",
  fontWeight: "bold",
};

const thCenter = { ...th, textAlign: "center" };
const thRight = { ...th, textAlign: "right" };

const td = {
  padding: 6,
  verticalAlign: "middle",
  borderLeft: "0px",
  borderRight: "0px",
};

const tdRight = { ...td, textAlign: "right" };

const detailTd = {
  padding: "0 6px 8px 6px",
  background: "#f8fafc",
};

const detailBox = {
  padding: "8px 10px",
  border: "1px solid #bfdbfe",
  background: "#eff6ff",
  color: "#1e3a8a",
  borderRadius: 4,
  fontSize: 12,
  lineHeight: 1.5,
};
